/**
 * src/components/Layout.js  (REPLACE)
 *
 * WHAT CHANGED (internals only — zero UI changes):
 *  - authFetch → Redux (useDashboard + useProject hooks)
 *  - fetchProjects on mount → loadProjects() thunk
 *  - fetchDashboard(sortBy) → loadProjects(sortBy) thunk
 *  - handleCreateProject → makeProject() thunk
 *  - useEffect watches createProjectSuccess to close modal + reset
 *  - projects/columns/setProjects/setColumns now come from Redux store
 *    and are passed down via Outlet context as before
 *    (so Dashboard, Projects, Kanban still receive them identically)
 */

import React, { useState, useRef, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import "../pages/styles/layout.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  offset,
  useFloating,
  autoUpdate,
  flip,
  shift,
  size,
  FloatingPortal,
} from "@floating-ui/react";
import Icons from "./icons";
import { safeJsonParse } from "../lib/api";
import useDashboard from "../hooks/useDashboard";
import useProject from "../hooks/useProject";
import useOrganisation from "../hooks/useOrganisation";
import { getMaxEstimatedHours } from "../lib/taskHours";

// Trigger wrapper + select-style dropdown, rendered through a portal (via
// FloatingPortal) so the panel is never clipped by an ancestor's
// `overflow-y-auto` — e.g. the scrollable Create Project card. Positioned
// with floating-ui so it always stays anchored to its trigger, matches the
// trigger's width, and stays inset from the viewport edges.
const FloatingSelectField = ({
  open,
  onToggle,
  onClose,
  options,
  onSelect,
  displayValue,
  className = "",
}) => {
  const { refs, floatingStyles } = useFloating({
    open,
    onOpenChange: (next) => {
      if (!next) onClose();
    },
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(6),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
        padding: 8,
      }),
    ],
  });

  return (
    <>
      <div
        ref={refs.setReference}
        className={`h-[42px] border border-gray-300 rounded-[8px] px-3 flex items-center justify-between cursor-pointer bg-white hover:bg-gray-50 transition-colors ${className}`}
        onClick={onToggle}
      >
        <span className="capitalize text-[14px] text-gray-800">
          {displayValue}
        </span>
        <Icons.DArrow className="w-3 h-3" />
      </div>
      {open && (
        <FloatingPortal>
          <div className="fixed inset-0 z-[9998]" onClick={onClose} />
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="z-[9999] bg-white border border-gray-300 rounded-[8px] shadow-lg overflow-hidden max-h-[220px] overflow-y-auto"
          >
            {options.map((item) => (
              <div
                key={item}
                className="px-3 py-3 hover:bg-blue-500 hover:text-white cursor-pointer capitalize text-[14px] transition-colors border-b border-gray-100 last:border-b-0"
                onClick={() => onSelect(item)}
              >
                {item}
              </div>
            ))}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

const Layout = () => {
  const [isOpen, setIsOpen] = useState(true);
  const location = useLocation();
const pageContentRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  // Which organisation (if any) the Create Project modal was opened from.
  // null for the normal sidebar flow.
  const [pendingOrganisationId, setPendingOrganisationId] = useState(null);
  const [step, setStep] = useState(1);
  const [formError, setFormError] = useState("");
  const endDateRef = useRef(null);
  const [showPriority, setShowPriority] = useState(false);
  const [showPriorityStep1, setShowPriorityStep1] = useState(false);
  const [showTaskType, setShowTaskType] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const taskEndDateRef = useRef(null);
  const taskStartDateRef = useRef(null);

  const [memberSearch, setMemberSearch] = useState("");
  const [showMembersDropdown, setShowMembersDropdown] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const {
    refs: membersFloatingRefs,
    floatingStyles: membersFloatingStyles,
  } = useFloating({
    open: showMembersDropdown,
    onOpenChange: setShowMembersDropdown,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(6),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
        padding: 8,
      }),
    ],
  });

  const [showAssignee, setShowAssignee] = useState(false);
  const assigneeDropdownRef = useRef(null);

  // The project-invite landing (see PublicRoute) applies to one sign-in only:
  // once inside the app, forget it so later sign-ins open the dashboard.
  useEffect(() => {
    try {
      localStorage.removeItem("qh_join_project");
    } catch {}
  }, []);

  useEffect(() => {
    const handleClickOutsideAssignee = (event) => {
      if (
        assigneeDropdownRef.current &&
        !assigneeDropdownRef.current.contains(event.target)
      ) {
        setShowAssignee(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideAssignee);

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideAssignee);
    };
  }, []);

  // ── Redux hooks ──────────────────────────────────────────────────
  const { projects, loadProjects } = useDashboard();
  const {
    columns,
    updateColumns,
    makeProject,
    isCreatingProject,
    createProjectError,
    createProjectSuccess,
    resetCreate,
    allUsers,
    loadAllUsers,
    clearUsers,
  } = useProject();
  const { loadOrganisation } = useOrganisation();

  // ── Form state (unchanged) ────────────────────────────────────────
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    priority: "medium",
    startDate: "",
    deadline: "",
  });

  const [taskData, setTaskData] = useState({
    listName: "",
    taskName: "",
    taskType: "design",
    priority: "medium",
    startDate: "",
    endDate: "",
    assignees: [],
    estimatedHours: "",
    description: "",
  });

  const handleToggleSidebar = () => setIsOpen((prev) => !prev);
  useEffect(() => {
  if (pageContentRef.current) {
    pageContentRef.current.scrollTop = 0;
  }
}, [location.pathname]);

  // ── Load projects and users on mount ───────────────────────────────
  useEffect(() => {
    clearUsers();
    loadProjects();
    loadAllUsers(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sort handler (replaces fetchDashboard(sortBy)) ────────────────
  const fetchDashboard = (sortBy = "") => {
    loadProjects(sortBy);
  };

  // ── Watch createProjectSuccess → close modal ──────────────────────
  useEffect(() => {
    if (createProjectSuccess) {
      const orgId = pendingOrganisationId;
      setShowModal(false);
      setStep(1);
      setFormError("");
      // Must be cleared here too — this effect closes the modal on success
      // instead of going through resetProjectModal().
      setPendingOrganisationId(null);
      setFormData({
        name: "",
        description: "",
        priority: "medium",
        startDate: "",
        deadline: "",
      });
      setTaskData({
        listName: "",
        taskName: "",
        taskType: "design",
        priority: "medium",
        startDate: "",
        endDate: "",
        assignees: [],
        estimatedHours: "",
        description: "",
      });
      // Re-fetch projects list so new project appears
      loadProjects();
      if (orgId) loadOrganisation(orgId);
      resetCreate();
    }
  }, [createProjectSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reset the whole Create Project modal back to a blank state ────
  const resetProjectModal = () => {
    setShowModal(false);
    setStep(1);
    setFormError("");
    // Clear the organisation context, or the next project created from the
    // sidebar would be filed under whichever org was last open.
    setPendingOrganisationId(null);
    setFormData({
      name: "",
      description: "",
      priority: "medium",
      startDate: "",
      deadline: "",
    });
    setTaskData({
      listName: "",
      taskName: "",
      taskType: "design",
      priority: "medium",
      startDate: "",
      endDate: "",
      assignees: [],
      estimatedHours: "",
      description: "",
    });
    setSelectedMembers([]);
    setMemberSearch("");
    setShowMembersDropdown(false);
    setShowPriority(false);
    setShowPriorityStep1(false);
    setShowTaskType(false);
    setShowAssignee(false);
  };

  // ── Load current user from localStorage (unchanged) ───────────────
  useEffect(() => {
    const user = safeJsonParse(localStorage.getItem("user"), null);
    setCurrentUser(user || { name: "Guest User", email: "guest@example.com" });
  }, []);

  // Text fields must be alphabetic/alphanumeric — not numbers only
  const hasLetter = (s) => /[a-zA-Z]/.test(s);

  // ── Validate step 1 (project details) ─────────────────────────────
  const validateStep1 = () => {
    if (!formData.name.trim()) return "*Project name is required.";
    if (!hasLetter(formData.name))
      return "*Project name cannot be numbers only.";
    if (!formData.description.trim())
      return "*Project description is required.";
    if (!hasLetter(formData.description))
      return "*Project description cannot be numbers only.";
    if (!formData.startDate) return "*Start date is required.";
    if (!formData.deadline) return "*End date is required.";
    if (
      new Date(formData.startDate).toDateString() ===
      new Date(formData.deadline).toDateString()
    )
      return "*Start date and end date cannot be the same.";
    if (!formData.priority) return "*Priority is required.";
    if (selectedMembers.length === 0)
      return "*At least one project member is required.";
    return "";
  };

  // ── Go to step 2 only when step 1 is complete ─────────────────────
  const handleNextStep = () => {
    const err = validateStep1();
    if (err) {
      setFormError(err);
      return;
    }
    setFormError("");
    setStep(2);
  };

  // ── Create project (replaces handleCreateProject) ─────────────────
  const handleCreateProject = () => {
    // Re-check step 1 in case the user jumped back
    const step1Err = validateStep1();
    if (step1Err) {
      setFormError(step1Err);
      setStep(1);
      return;
    }

    // Validate all step 2 (task) fields
    const step2Err = (() => {
      if (!taskData.listName.trim()) return "*List name is required.";
      if (!hasLetter(taskData.listName))
        return "*List name cannot be numbers only.";
      if (!taskData.taskName.trim()) return "*Task name is required.";
      if (!hasLetter(taskData.taskName))
        return "*Task name cannot be numbers only.";
      if (!taskData.taskType) return "*Task type is required.";
      if (!taskData.priority) return "*Task priority is required.";
      if (!taskData.startDate) return "*Task start date is required.";
      if (!taskData.endDate) return "*Task end date is required.";
      if (!taskData.assignees || taskData.assignees.length === 0)
        return "*At least one task assignee is required.";
      if (!taskData.estimatedHours || parseInt(taskData.estimatedHours) <= 0)
        return "*Estimated hours is required.";
      if (taskData.startDate && taskData.endDate) {
        const maxHours = getMaxEstimatedHours(
          taskData.startDate,
          taskData.endDate,
        );
        if (parseInt(taskData.estimatedHours) > maxHours) {
          return `*Estimated hours cannot exceed ${maxHours} (max 8 hours/day for the selected dates).`;
        }
      }

      if (!taskData.description.trim()) return "*Task description is required.";
      return "";
    })();
    if (step2Err) {
      setFormError(step2Err);
      return;
    }
    setFormError("");

    // Normalize picked dates to noon UTC of the calendar day. The picker
    // stamps dates with the click-time, so a valid start/end pair could
    // serialize with end < start and fail the backend date comparison.
    const toDateOnlyISO = (d) => {
      if (!d) return null;
      const dt = new Date(d);
      return new Date(
        Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate(), 12),
      ).toISOString();
    };

    const requestBody = {
      name: formData.name,
      description: formData.description,
      startDate: toDateOnlyISO(formData.startDate) || new Date().toISOString(),
      deadline: toDateOnlyISO(formData.deadline) || "",
      priority: formData.priority,
      members: selectedMembers.map((m) => m._id),
      list: { name: taskData.listName },
      task: {
        name: taskData.taskName,
        taskType: taskData.taskType,
        assignees: taskData.assignees,
        estimatedHours: taskData.estimatedHours
          ? parseInt(taskData.estimatedHours)
          : 0,
        description: taskData.description,
        priority: taskData.priority,
        taskStartDate: toDateOnlyISO(taskData.startDate),
        taskDeadline: toDateOnlyISO(taskData.endDate),
      },
      // Set only when the modal was opened from an organisation page, so the
      // new project is filed under that org. Omitted entirely for the normal
      // sidebar flow, which keeps creating ungrouped projects exactly as before.
      ...(pendingOrganisationId ? { organisation: pendingOrganisationId } : {}),
    };

    // makeProject dispatches createProject thunk
    // useEffect above handles closing modal on success
    makeProject(requestBody);
  };

  // ── setProjects/setColumns wrappers for Outlet context ────────────
  // Outlet consumers (Dashboard, Projects, Kanban) call these
  // exactly as before — they just update Redux now instead of local state
  const setProjects = (val) => {
    // If called with a function (prev => ...) resolve it first
    // Some components still call setProjects(res.data) directly
    // We keep this as a no-op since projects live in Redux now
    // The real updates come from loadProjects() dispatches
  };

  const setColumns = (val) => {
    if (typeof val === "function") {
      updateColumns(val(columns));
    } else {
      updateColumns(val);
    }
  };

  // ── JSX (100% identical to original) ─────────────────────────────
  return (
    <div className={`layout ${isOpen ? "sidebar-open" : "sidebar-collapsed"}`}>
      {/* Overlay for mobile — closes sidebar when tapped */}
      <div className="sidebar-overlay" onClick={handleToggleSidebar} />
      <Sidebar
        isOpen={isOpen}
        toggleSidebar={handleToggleSidebar}
        currentUser={currentUser}
        onNewProject={() => {
          setPendingOrganisationId(null);
          setShowModal(true);
          setStep(1);
          setFormError("");
        }}
      />
<div className="main">
        <Topbar currentUser={currentUser} toggleSidebar={handleToggleSidebar} />
<div className="page-content" ref={pageContentRef}>
            <Outlet
            context={{
              projects,
              setProjects,
              columns,
              setColumns,
              fetchDashboard,
              currentUser,
              // Lets the Organisation page reuse this same two-step Create
              // Project wizard instead of duplicating it. Passing an
              // organisationId files the resulting project under that org.
              openCreateProject: (organisationId = null) => {
                setPendingOrganisationId(organisationId);
                setShowModal(true);
                setStep(1);
                setFormError("");
              },
            }}
          />
        </div>
      </div>

      {/* ===== MODAL ===== */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex flex-col items-center justify-center z-50 h-full">
          <div
            className={`bg-white rounded-xl p-4 sm:p-6 shadow-lg relative overflow-y-auto ${
              step === 1
                ? "w-[95vw] max-w-[887px] min-h-[592px] max-h-[90vh]"
                : "w-[95vw] max-w-[864px] min-h-[781px] max-h-[90vh]"
            }`}
          >
            <button
              className="absolute top-[24px] right-[24px] text-gray-500 text-lg"
              onClick={resetProjectModal}
            >
              ✕
            </button>

            {/* STEP 1 */}
            {step === 1 && (
              <>
                <h2 className="font-semibold text-[28px] font-[Poppins]  bg-white pt-[16px]">
                  Create New Project
                </h2>
                <p className="text-gray-500 mb-6 font-normaltext-[16px] font-[Poppins] border-b pt-[8px] pb-[8px]">
                  Set up your project and start managing work efficiently.
                </p>

                {createProjectError && (
                  <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                    {createProjectError}
                  </div>
                )}

                {formError && (
                  <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                    {formError}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[16px] font-normal text-black leading-[100%] mb-[6px] block">
                      Project Name
                    </label>

                    <div className="relative mt-[6px]">
                      {/* Icon Box */}
                      <div className="absolute left-0 top-0 h-[42px] w-[42px] flex items-center justify-center rounded-l-[8px] bg-[#0404040A] border border-gray-300 border-r-0">
                        <Icons.Folder />
                      </div>

                      {/* Input */}
                      <input
                        style={{ paddingLeft: "50px" }}
                        className="w-full lg:w-[392px] h-[42px] border border-gray-300 rounded-[8px] text-[14px]"
                        placeholder="Enter your Project Name"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  {/* PROJECT DESCRIPTION */}
                  <div>
                    <label className="text-[16px] font-normal text-black leading-[100%] mb-[6px] block">
                      Project Description
                    </label>

                    <div className="relative mt-[6px] w-full lg:w-[392px]">
                      <input
                        className="w-full h-[40px] border border-gray-300 rounded-[8px] px-3 text-[14px]"
                        placeholder="Enter your Project Description"
                        maxLength={50}
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                      />

                      <p className="text-xs text-gray-400 text-right mt-1">
                        {formData.description.length}/50
                      </p>
                    </div>
                  </div>

                  {/* START DATE */}
                  <div>
                    <label className="text-[16px] font-normal text-black leading-[100%] mb-[6px] block">
                      Start Date
                    </label>

                    <div className="relative mt-[6px]">
                      {/* Icon */}
                      <div
                        className="absolute left-0 top-0 h-[42px] w-[42px] flex items-center justify-center rounded-l-[8px] bg-[#0404040A] border border-gray-300 border-r-0 cursor-pointer z-10"
                        onClick={() =>
                          document.getElementById("startDatePicker").focus()
                        }
                      >
                        <Icons.Calendar />
                      </div>
                      <DatePicker
                        id="startDatePicker"
                        selected={
                          formData.startDate
                            ? new Date(formData.startDate)
                            : null
                        }
                        onChange={(date) =>
                          setFormData({ ...formData, startDate: date })
                        }
                        placeholderText="Select start date"
                        className="w-full lg:w-[392px] h-[42px] border border-gray-300 rounded-[8px] pl-[50px] pr-3"
                        dateFormat="dd MMM yyyy"
                        popperPlacement="bottom-start"
                        popperClassName="z-[60]"
                        showPopperArrow={false}
                        popperProps={{ middleware: [offset(6)] }}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                      />
                    </div>
                  </div>

                  {/* END DATE */}
                  <div>
                    <label className="text-[16px] font-normal text-black leading-[100%] mb-[6px] block">
                      End Date
                    </label>

                    <div className="relative mt-[6px]">
                      {/* Icon */}
                      <div
                        className="absolute left-0 top-0 h-[42px] w-[42px] flex items-center justify-center rounded-l-[8px] bg-[#0404040A] border border-gray-300 border-r-0 cursor-pointer z-10"
                        onClick={() => endDateRef.current.setFocus()}
                      >
                        <Icons.Calendar />
                      </div>
                      <DatePicker
                        ref={endDateRef}
                        selected={formData.deadline}
                        onChange={(date) =>
                          setFormData({ ...formData, deadline: date })
                        }
                        placeholderText="Select end date"
                        className="w-full lg:w-[392px] h-[42px] border border-gray-300 rounded-[8px] pl-[50px] pr-3"
                        dateFormat="dd MMM yyyy"
                        minDate={formData.startDate || null}
                        popperPlacement="bottom-start"
                        popperClassName="z-[60]"
                        showPopperArrow={false}
                        popperProps={{ middleware: [offset(6)] }}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                      />
                    </div>
                  </div>

                  {/* PRIORITY */}
                  <div className="py-2">
                    <label className="text-[16px] font-normal text-black leading-[100%] mb-[6px] block">
                      Priority
                    </label>

                    <div className="relative w-full lg:w-[392px]">
                      <FloatingSelectField
                        className="w-full"
                        open={showPriorityStep1}
                        onToggle={() => setShowPriorityStep1((prev) => !prev)}
                        onClose={() => setShowPriorityStep1(false)}
                        options={["low", "medium", "high", "critical"]}
                        displayValue={formData.priority}
                        onSelect={(item) => {
                          setFormData({ ...formData, priority: item });
                          setShowPriorityStep1(false);
                        }}
                      />
                    </div>
                  </div>

                  {/* PROJECT MEMBERS */}
                  <div className="py-2">
                    <label className="text-[16px] font-normal text-black leading-[100%] mb-[6px] block">
                      Project Members
                    </label>

                    <div
                      className="relative mt-[6px]"
                      ref={membersFloatingRefs.setReference}
                    >
                      {/* Search Icon */}
                      <div className="absolute left-3 top-[20px] -translate-y-1/2 text-gray-400 z-10">
                        <Icons.Search className="w-4 h-4" />
                      </div>

                      {/* INPUT */}
                      <input
                        type="text"
                        placeholder="Search Members"
                        className="w-full lg:w-[392px] h-[40px] border border-gray-300 rounded-[8px] pl-9 pr-3 text-[14px]"
                        value={memberSearch}
                        onClick={() => setShowMembersDropdown((prev) => !prev)}
                        onChange={(e) => {
                          setMemberSearch(e.target.value);
                          setShowMembersDropdown(true);
                        }}
                      />
                    </div>

                    {/* DROPDOWN — rendered through a portal so it is never
                        clipped by the scrollable Create Project card */}
                    {showMembersDropdown && (
                      <FloatingPortal>
                        <div
                          className="fixed inset-0 z-[9998]"
                          onClick={() => setShowMembersDropdown(false)}
                        />
                        <div
                          ref={membersFloatingRefs.setFloating}
                          style={membersFloatingStyles}
                          className="z-[9999] bg-white border border-gray-200 rounded-[8px] shadow-lg overflow-hidden"
                        >
                          {/* MEMBERS LIST */}
                          <div className="max-h-[220px] overflow-y-auto">
                            {allUsers
                              .filter((member) =>
                                member.name
                                  ?.toLowerCase()
                                  .includes(memberSearch.toLowerCase()),
                              )
                              .map((member) => {
                                const isSelected = selectedMembers.some(
                                  (m) => m._id === member._id,
                                );

                                return (
                                  <div
                                    key={member._id}
                                    className="px-3 py-3 hover:bg-[#F5F7FF] cursor-pointer flex items-center justify-between border-b border-gray-100"
                                    onClick={() => {
                                      if (isSelected) {
                                        // REMOVE MEMBER
                                        setSelectedMembers((prev) =>
                                          prev.filter(
                                            (m) => m._id !== member._id,
                                          ),
                                        );

                                        setFormData({
                                          ...formData,
                                          members: (
                                            formData.members || []
                                          ).filter((id) => id !== member._id),
                                        });
                                      } else {
                                        // ADD MEMBER
                                        setSelectedMembers((prev) => [
                                          ...prev,
                                          member,
                                        ]);

                                        setFormData({
                                          ...formData,
                                          members: [
                                            ...(formData.members || []),
                                            member._id,
                                          ],
                                        });
                                      }
                                    }}
                                  >
                                    {/* LEFT */}
                                    <div className="flex items-center gap-3">
                                      <div className="w-[24px] h-[24px] rounded-full border border-gray-400 flex items-center justify-center">
                                        <Icons.Profile />
                                      </div>

                                      <div>
                                        <p className="text-[14px] font-medium text-black">
                                          {member.name}
                                        </p>
                                      </div>
                                    </div>

                                    {/* RIGHT TICK */}
                                    <div
                                      className={`w-[16px] h-[16px] rounded-[4px] border flex items-center justify-center ${
                                        isSelected
                                          ? "bg-blue-500 border-blue-500"
                                          : "border-gray-300"
                                      }`}
                                    >
                                      {isSelected && (
                                        <svg
                                          width="10"
                                          height="10"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="white"
                                          strokeWidth="3"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}

                            {/* EMPTY STATE */}
                            {allUsers.filter((member) =>
                              member.name
                                ?.toLowerCase()
                                .includes(memberSearch.toLowerCase()),
                            ).length === 0 && (
                              <div className="px-3 py-3 text-gray-400 text-sm">
                                No members found
                              </div>
                            )}
                          </div>
                        </div>
                      </FloatingPortal>
                    )}

                    {/* SELECTED MEMBERS */}
                    {selectedMembers.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {selectedMembers.map((member) => (
                          <div
                            key={member._id}
                            className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                          >
                            <span>{member.name}</span>

                            <button
                              type="button"
                              className="absolute top-[38px] right-[38px] text-gray-500 hover:text-gray-700"
                              onClick={() => {
                                setSelectedMembers((prev) =>
                                  prev.filter((m) => m._id !== member._id),
                                );

                                setFormData({
                                  ...formData,
                                  members: (formData.members || []).filter(
                                    (id) => id !== member._id,
                                  ),
                                });
                              }}
                            ></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6 sm:mt-10">
                  <button
                    className="px-4 py-2 border rounded-md w-full lg:w-[163px] h-[40px]"
                    onClick={resetProjectModal}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-8 py-2 bg-[#4253C7] text-white rounded-md w-full md:w-[163px] h-[40px]"
                    onClick={handleNextStep}
                  >
                    Next step
                  </button>
                </div>
              </>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <>
                <h2 className="text-[28px] font-semibold leading-[100%] w-[147px] h-[42px] top-[71px] left-[320px] pt-4 ">
                  Add Tasks
                </h2>
                <p className="text-gray-500 mb-6 font-normaltext-[16px] font-[Poppins] pt-[16px] pb-[8px] ">
                  Define tasks for your project workflow.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[16px] font-normal text-black leading-[100%] mb-[6px] block">
                      List Name
                    </label>
                    <input
                      className="w-full lg:w-[392px] h-[42px] border border-gray-300 rounded-[8px] px-3 text-[14px] placeholder:text-gray-400"
                      placeholder="Enter your list name"
                      onChange={(e) =>
                        setTaskData({ ...taskData, listName: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-[16px] font-normal text-black leading-[100%] mb-[6px] block">
                      Task Name
                    </label>
                    <input
                      className="w-full lg:w-[392px] h-[42px] border border-gray-300 rounded-[8px] px-3 text-[14px] placeholder:text-gray-400"
                      placeholder="Enter your task name"
                      onChange={(e) =>
                        setTaskData({ ...taskData, taskName: e.target.value })
                      }
                    />
                  </div>

                  <div>
                    <label className="text-[16px] font-normal text-black leading-[100%] mb-[6px] block">
                      Task Type
                    </label>

                    <div className="relative w-full lg:w-[392px]">
                      <FloatingSelectField
                        className="w-full"
                        open={showTaskType}
                        onToggle={() => setShowTaskType((prev) => !prev)}
                        onClose={() => setShowTaskType(false)}
                        options={[
                          "design",
                          "frontend",
                          "backend",
                          "testing",
                          "deployment",
                          "requirements",
                          "others",
                        ]}
                        displayValue={taskData.taskType}
                        onSelect={(item) => {
                          setTaskData({ ...taskData, taskType: item });
                          setShowTaskType(false);
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[16px] font-normal text-black leading-[100%] mb-[6px] block">
                      Priority
                    </label>
                    <div className="relative w-full lg:w-[392px]">
                      <FloatingSelectField
                        className="w-full"
                        open={showPriority}
                        onToggle={() => setShowPriority((prev) => !prev)}
                        onClose={() => setShowPriority(false)}
                        options={["low", "medium", "high", "critical"]}
                        displayValue={taskData.priority}
                        onSelect={(item) => {
                          setTaskData({ ...taskData, priority: item });
                          setShowPriority(false);
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[16px] font-normal text-black leading-[100%]">
                      Start Date
                    </label>

                    <div className="relative mt-1">
                      {/* Icon */}
                      <div
                        className="absolute left-0 top-0 h-[42px] w-[42px] flex items-center justify-center rounded-l-[8px] bg-[#0404040A] border border-gray-300 border-r-0 cursor-pointer z-10"
                        onClick={() => taskStartDateRef.current.setFocus()}
                      >
                        <Icons.Calendar />
                      </div>

                      <DatePicker
                        ref={taskStartDateRef}
                        selected={
                          taskData.startDate
                            ? new Date(taskData.startDate)
                            : null
                        }
                        onChange={(date) =>
                          setTaskData({
                            ...taskData,
                            startDate: date,
                          })
                        }
                        placeholderText="Select start date"
                        className="w-full lg:w-[392px] h-[42px] border border-gray-300 rounded-[8px] pl-[50px] pr-3 text-[14px] outline-none"
                        dateFormat="dd MMM yyyy"
                        minDate={formData.startDate || null}
                        popperPlacement="bottom-start"
                        popperClassName="z-[60]"
                        showPopperArrow={false}
                        popperProps={{ middleware: [offset(6)] }}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[16px] font-normal text-black leading-[100%]">
                      End Date
                    </label>

                    <div className="relative mt-1">
                      {/* Icon */}
                      <div
                        className="absolute left-0 top-0 h-[42px] w-[42px] flex items-center justify-center rounded-l-[8px] bg-[#0404040A] border border-gray-300 border-r-0 cursor-pointer z-10"
                        onClick={() => taskEndDateRef.current.setFocus()}
                      >
                        <Icons.Calendar />
                      </div>

                      <DatePicker
                        ref={taskEndDateRef}
                        selected={
                          taskData.endDate ? new Date(taskData.endDate) : null
                        }
                        onChange={(date) =>
                          setTaskData({
                            ...taskData,
                            endDate: date,
                          })
                        }
                        placeholderText="Select end date"
                        className="w-full lg:w-[392px] h-[42px] border border-gray-300 rounded-[8px] pl-[50px] pr-3 text-[14px] outline-none"
                        dateFormat="dd MMM yyyy"
                        minDate={
                          taskData.startDate || formData.startDate || null
                        }
                        popperPlacement="bottom-start"
                        popperClassName="z-[60]"
                        showPopperArrow={false}
                        popperProps={{ middleware: [offset(6)] }}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[16px] font-normal text-black leading-[100%] mb-[6px] block">
                      Task Assignee
                    </label>

                    <div
                      className="relative w-full lg:w-[392px]"
                      ref={assigneeDropdownRef}
                    >
                      {/* Trigger */}
                      <div
                        className="w-full h-[42px] border border-gray-300 rounded-[8px] px-3 flex items-center justify-between cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                        onClick={() => setShowAssignee((prev) => !prev)}
                      >
                        <span className="text-[14px] text-gray-800 truncate">
                          {taskData.assignees?.length > 0
                            ? selectedMembers
                                .filter((m) =>
                                  taskData.assignees.includes(m._id),
                                )
                                .map((m) => m.name)
                                .join(", ")
                            : "Select Assignee"}
                        </span>

                        <Icons.DArrow className="w-3 h-3" />
                      </div>

                      {showAssignee && (
                        <div className="absolute top-[48px] left-0 w-full lg:w-[392px] bg-white border border-gray-200 rounded-[8px] shadow-lg z-50 overflow-hidden">
                          <div className="max-h-[220px] overflow-y-auto">
                            {selectedMembers.map((member) => {
                              const isSelected = taskData.assignees?.includes(
                                member._id,
                              );

                              return (
                                <div
                                  key={member._id}
                                  className="px-3 py-3 hover:bg-[#F5F7FF] cursor-pointer flex items-center justify-between border-b border-gray-100"
                                  onClick={() => {
                                    const alreadySelected =
                                      taskData.assignees?.includes(member._id);

                                    setTaskData({
                                      ...taskData,
                                      assignees: alreadySelected
                                        ? taskData.assignees.filter(
                                            (id) => id !== member._id,
                                          )
                                        : [
                                            ...(taskData.assignees || []),
                                            member._id,
                                          ],
                                    });
                                  }}
                                >
                                  {/* LEFT */}
                                  <div className="flex items-center gap-3">
                                    <div className="w-[24px] h-[24px] rounded-full border border-gray-400 flex items-center justify-center">
                                      <Icons.Profile />
                                    </div>

                                    <div>
                                      <p className="text-[14px] font-medium text-black">
                                        {member.name}
                                      </p>
                                    </div>
                                  </div>

                                  {/* RIGHT TICK */}
                                  <div
                                    className={`w-[16px] h-[16px] rounded-[4px] border flex items-center justify-center ${
                                      isSelected
                                        ? "bg-blue-500 border-blue-500"
                                        : "border-gray-300"
                                    }`}
                                  >
                                    {isSelected && (
                                      <svg
                                        width="10"
                                        height="10"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="white"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <polyline points="20 6 9 17 4 12" />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-[16px] font-normal text-black leading-[100%] mb-[6px] block">
                      Estimated Hours
                    </label>
                    <input
                      className="w-full lg:w-[392px] h-[42px] border border-gray-300 rounded-[8px] px-3 text-[14px] placeholder:text-gray-400"
                      placeholder="Enter estimated hours"
                      type="number"
                      value={taskData.estimatedHours}
                      min={1}
                      max={getMaxEstimatedHours(
                        taskData.startDate,
                        taskData.endDate,
                      )}
                      onChange={(e) => {
                        const maxHours = getMaxEstimatedHours(
                          taskData.startDate,
                          taskData.endDate,
                        );
                        const raw = e.target.value;
                        const capped =
                          raw !== "" && Number(raw) > maxHours
                            ? String(maxHours)
                            : raw;
                        setTaskData({
                          ...taskData,
                          estimatedHours: capped,
                        });
                      }}
                    />
                    {taskData.startDate && taskData.endDate && (
                      <p className="text-xs text-gray-400 mt-1">
                        Max{" "}
                        {getMaxEstimatedHours(
                          taskData.startDate,
                          taskData.endDate,
                        )}{" "}
                        hours (8 hours/day) for the selected dates.
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <label className="text-[16px] font-normal text-black leading-[100%] mb-[6px] block">
                    Description
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-[8px] px-3 py-2 text-[14px] placeholder:text-gray-400 resize-none"
                    rows="5"
                    placeholder="Enter your message..."
                    value={taskData.description}
                    onChange={(e) =>
                      setTaskData({ ...taskData, description: e.target.value })
                    }
                  />
                </div>
                {formError && (
                  <div className="mb-3 mt-4 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                    {formError}
                  </div>
                )}

                <div className="flex justify-end gap-3 mt-8 ">
                  <button
                    className="px-12 py-2 border rounded-md lg:w-[163px] h-[40px]"
                    onClick={() => setStep(1)}
                  >
                    Back
                  </button>
                  <button
                    className="px-8 py-2 bg-[#4253C7] text-white rounded-md"
                    onClick={handleCreateProject}
                    disabled={isCreatingProject}
                  >
                    {isCreatingProject ? "Creating..." : "Create Project"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;


